// MB-1460 — pure word-token edit model for the ZapCap caption editor.
//
// The dialog renders these tokens inline; every mutation goes through the
// reducer so the edit rules are enforced by construction:
//   - word REPLACEMENT only (a token's text changes, its position never does),
//   - deletion allowed (marked, so it can be restored before submitting),
//   - NO insertion — there is no action that grows the token set.
// Timings are never touched client-side: submission sends only {id, text}
// pairs and the api rebuilds the full entries from the STORED transcript.

import { ZapcapTranscriptWord } from "../../../types/zapcapTranscript";

// Words at or below this transcription confidence get the "suspect" highlight.
export const LOW_CONFIDENCE_THRESHOLD = 0.7;

export interface CaptionToken {
    id: string;
    text: string;
    originalText: string;
    type: "word" | "punctuation";
    confidence: number;
    deleted: boolean;
}

export type CaptionEditorAction =
    | { type: "reset"; words: ZapcapTranscriptWord[] }
    | { type: "edit"; id: string; text: string }
    | { type: "delete"; id: string }
    | { type: "restore"; id: string };

export function tokensFromWords(
    words: ZapcapTranscriptWord[]
): CaptionToken[] {
    return words.map((w) => ({
        id: w.id,
        text: w.text,
        originalText: w.text,
        type: w.type,
        confidence: w.confidence,
        deleted: false,
    }));
}

export function isLowConfidence(token: CaptionToken): boolean {
    return token.type === "word" && token.confidence <= LOW_CONFIDENCE_THRESHOLD;
}

export function captionEditorReducer(
    tokens: CaptionToken[],
    action: CaptionEditorAction
): CaptionToken[] {
    switch (action.type) {
        case "reset":
            return tokensFromWords(action.words);
        case "edit":
            return tokens.map((t) =>
                t.id === action.id ? { ...t, text: action.text } : t
            );
        case "delete":
            return tokens.map((t) =>
                t.id === action.id ? { ...t, deleted: true } : t
            );
        case "restore":
            return tokens.map((t) =>
                t.id === action.id
                    ? { ...t, deleted: false, text: t.text || t.originalText }
                    : t
            );
        default:
            return tokens;
    }
}

export function hasChanges(tokens: CaptionToken[]): boolean {
    return tokens.some(
        (t) => t.deleted || t.text.trim() !== t.originalText.trim()
    );
}

export function canSubmit(tokens: CaptionToken[]): boolean {
    const survivors = tokens.filter((t) => !t.deleted);
    if (survivors.length === 0) return false;
    return survivors.every((t) => t.text.trim().length > 0);
}

export function toSubmitWords(
    tokens: CaptionToken[]
): Array<{ id: string; text: string }> {
    return tokens
        .filter((t) => !t.deleted)
        .map((t) => ({ id: t.id, text: t.text.trim() }));
}
